/**
 * M3's station service tabs as `StationServicePanel`s (M3 plan R16, §2.6.5): Charts (`chart-buyer`), Parts
 * (`parts-vendor`), Scanner (`outfitting`) and Home (every station). Any host mounts them: M3's hail panel now,
 * M2's station screen after the bridge. Each tab renders a view model (model.ts) into its element with plain DOM,
 * sends a button's command through `core.command`, and shows the outcome inline ("Not enough credits", "Hold
 * full"); then it re-renders and calls `ctx.refresh()` so the host re-reads its buttons. UI events only.
 */
import type { StationSite } from '../../../galaxy/layout/types'
import { settledOf, type PanelContext, type StationServicePanel } from '../../ports'
import type { ReachCore } from '../../runtime/types'
import type { StandInPrices } from '../../standin'
import type { ServicesExternals } from './external'
import {
  chartsView,
  homeView,
  partsView,
  resultMessage,
  scannerView,
  SERVICE_TABS,
  tabAvailable,
  type InlineMessage,
  type PanelView,
  type ServiceTabDef,
  type ServiceTabId,
  type ViewAction,
  type ViewLine,
} from './model'

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  e.className = className
  if (text !== undefined) e.textContent = text
  return e
}

function lineElement(l: ViewLine): HTMLElement {
  const row = element('div', l.tone && l.tone !== 'normal' ? `rs-line ${l.tone}` : 'rs-line')
  row.append(element('span', 'rs-label', l.label), element('span', 'rs-value', l.value))
  if (l.note !== undefined) row.append(element('span', 'rs-note', l.note))
  return row
}

/** Renders a view into `el`, replacing what was there. Enabled buttons call `onAction`. */
export function renderPanelView(el: HTMLElement, view: PanelView, message: InlineMessage | null, onAction: (a: ViewAction) => void): void {
  const nodes: HTMLElement[] = []
  if (view.summary.length > 0) {
    const summary = element('div', 'rs-summary')
    for (const l of view.summary) summary.append(lineElement(l))
    nodes.push(summary)
  }
  if (view.empty !== null) nodes.push(element('p', 'rs-empty', view.empty))
  for (const g of view.groups) {
    const group = element('section', 'rs-group')
    if (g.heading !== null) group.append(element('h3', 'rs-heading', g.heading))
    for (const l of g.lines) group.append(lineElement(l))
    if (g.actions.length > 0) {
      const row = element('div', 'rs-actions')
      for (const a of g.actions) {
        const b = element('button', a.primary ? 'rs-action primary' : 'rs-action', a.label)
        b.type = 'button'
        b.tabIndex = -1
        b.disabled = !a.enabled
        b.dataset.action = a.id
        b.addEventListener('click', () => {
          if (a.enabled) onAction(a)
        })
        row.append(b)
      }
      group.append(row)
    }
    nodes.push(group)
  }
  if (message !== null) {
    const m = element('p', message.ok ? 'rs-message ok' : 'rs-message error', message.text)
    m.setAttribute('role', 'status')
    nodes.push(m)
  }
  el.replaceChildren(...nodes)
}

export interface ServicePanelDeps {
  prices: StandInPrices
  externals: ServicesExternals
}

/** The view for one tab. */
export function tabView(id: ServiceTabId, core: ReachCore, station: StationSite, deps: ServicePanelDeps): PanelView {
  switch (id) {
    case 'charts':
      return chartsView(core, station, deps.externals.firstDiscoveryMult)
    case 'parts':
      return partsView(core.career, deps.prices, station)
    case 'scanner':
      return scannerView(core.career, deps.prices, deps.externals.scannerRangesM, station)
    case 'home':
      return homeView(core, station)
  }
}

function viewPanel(tab: ServiceTabDef, deps: ServicePanelDeps): StationServicePanel {
  return {
    id: tab.id,
    title: tab.title,
    order: tab.order,
    available: (station: StationSite): boolean => tabAvailable(tab, station),
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      let message: InlineMessage | null = null
      let mounted = true
      const render = (): void => renderPanelView(el, tabView(tab.id, ctx.core, ctx.station, deps), message, act)
      function act(a: ViewAction): void {
        if (!mounted) return
        const result = ctx.core.command(a.command)
        message = resultMessage(result)
        render()
        ctx.refresh()
        // A strategic Parts-tab buy (SY-2e) settles once the worker answered: show that outcome when it lands.
        const settled = settledOf(result)
        if (settled !== null) {
          void settled.then((later) => {
            if (!mounted) return
            message = resultMessage(later)
            render()
            ctx.refresh()
          })
        }
      }
      render()
      return (): void => {
        mounted = false
        el.replaceChildren()
      }
    },
  }
}

/** The four M3 tabs, ready to register into `ctx.reach.services`. */
export function createServicePanels(deps: ServicePanelDeps): StationServicePanel[] {
  return SERVICE_TABS.map((tab) => viewPanel(tab, deps))
}
